import React, { useEffect, useState } from "react";
import {
  Row, Col, Card, Select, Button,
  Table, notification, Spin, Progress, Tag, Statistic, Space
} from "antd";
import axios from "axios";
import { baseUrl } from "../../config";
import {
  BarChartOutlined, DollarOutlined,
  WarningOutlined, CheckCircleOutlined, ReloadOutlined
} from "@ant-design/icons";

const { Option } = Select;

const fmt = (amount) => `₹${Number(amount || 0).toLocaleString('en-IN')}`;

function BudgetReports() {
  const now = new Date();
  const defaultFY = now.getMonth() >= 3
    ? `${now.getFullYear()}-${String(now.getFullYear()+1).slice(-2)}`
    : `${now.getFullYear()-1}-${String(now.getFullYear()).slice(-2)}`;

  // Default to last FY — most likely to have real data
  const lastFYStartYear = now.getMonth() >= 3 ? now.getFullYear() - 1 : now.getFullYear() - 2;
  const lastFY = `${lastFYStartYear}-${String(lastFYStartYear + 1).slice(-2)}`;

  const [loading, setLoading] = useState(false);
  const [fiscalYear, setFiscalYear] = useState(lastFY);
  const [budgetData, setBudgetData] = useState([]);
  const [summaryStats, setSummaryStats] = useState({ totalAllocated: 0, totalSpent: 0, totalRemaining: 0, avgUtilization: 0 });

  const columns = [
    { title: 'Purpose', dataIndex: 'purpose_name', key: 'purpose_name',
      render: (name, record) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{name}</div>
          <Tag color="orange" style={{ fontSize: 10 }}>{record.purpose_category?.replace('_', ' ')}</Tag>
        </div>
      ) },
    { title: 'Allocated', dataIndex: 'allocated_amount', key: 'allocated_amount',
      render: (v) => <span style={{ color: '#7a0000', fontWeight: 600, fontSize: 12 }}>{fmt(v)}</span> },
    { title: 'Spent', dataIndex: 'spent_amount', key: 'spent_amount',
      render: (v) => <span style={{ color: '#52c41a', fontWeight: 600, fontSize: 12 }}>{fmt(v)}</span> },
    { title: 'Remaining', dataIndex: 'remaining_amount', key: 'remaining_amount',
      render: (v) => <span style={{ color: '#fa8c16', fontWeight: 600, fontSize: 12 }}>{fmt(v)}</span> },
    { title: 'Utilization', dataIndex: 'utilization_percentage', key: 'utilization_percentage',
      render: (pct) => {
        const p = parseFloat(pct) || 0;
        const color = p > 90 ? '#ff4d4f' : p > 75 ? '#fa8c16' : '#52c41a';
        return <div style={{ width: 100 }}><Progress percent={Math.round(p)} size="small" strokeColor={color} /></div>;
      } },
    { title: 'Status', key: 'budget_status',
      render: (_, record) => {
        const u = parseFloat(record.utilization_percentage) || 0;
        if (u > 95) return <Tag color="error" icon={<WarningOutlined />}>Over Budget</Tag>;
        if (u > 80) return <Tag color="warning">High Usage</Tag>;
        if (u > 0) return <Tag color="success" icon={<CheckCircleOutlined />}>On Track</Tag>;
        return <Tag color="default">Not Started</Tag>;
      } },
  ];

  useEffect(() => { fetchBudgetReport(); }, [fiscalYear]);

  const fetchBudgetReport = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/expenditure-reports/budget-utilization?fiscal_year=${fiscalYear}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      if (response.data.status) {
        const data = response.data.result;
        setBudgetData(data);
        const totals = data.reduce((acc, item) => {
          acc.totalAllocated += parseFloat(item.allocated_amount) || 0;
          acc.totalSpent += parseFloat(item.spent_amount) || 0;
          acc.totalRemaining += parseFloat(item.remaining_amount) || 0;
          return acc;
        }, { totalAllocated: 0, totalSpent: 0, totalRemaining: 0 });
        totals.avgUtilization = totals.totalAllocated > 0 ? (totals.totalSpent / totals.totalAllocated) * 100 : 0;
        setSummaryStats(totals);
      }
    } catch {
      notification.error({ message: 'Error', description: 'Failed to fetch budget report' });
    } finally {
      setLoading(false);
    }
  };

  const getFiscalYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => {
      const y = currentYear - 2 + i;
      const fy = `${y}-${(y + 1).toString().slice(-2)}`;
      return <Option key={fy} value={fy}>FY {fy}</Option>;
    });
  };

  const getStats = () => {
    const highUsage = budgetData.filter(item => parseFloat(item.utilization_percentage) > 80).length;
    const onTrack = budgetData.filter(item => { const u = parseFloat(item.utilization_percentage); return u > 0 && u <= 80; }).length;
    const notStarted = budgetData.filter(item => parseFloat(item.utilization_percentage) === 0).length;
    return { highUsage, onTrack, notStarted };
  };

  const stats = getStats();

  const statCards = [
    { label: 'Total Allocated', value: fmt(summaryStats.totalAllocated), accent: '#7a0000', bg: '#eef2f8', icon: <DollarOutlined /> },
    { label: 'Total Spent',     value: fmt(summaryStats.totalSpent),     accent: '#15803d', bg: '#dcfce7', icon: <CheckCircleOutlined /> },
    { label: 'Remaining',       value: fmt(summaryStats.totalRemaining), accent: '#d97706', bg: '#fef3c7', icon: <WarningOutlined /> },
    { label: 'Utilization',     value: `${Math.round(summaryStats.avgUtilization)}%`, accent: summaryStats.avgUtilization > 80 ? '#dc2626' : '#0891b2', bg: summaryStats.avgUtilization > 80 ? '#fee2e2' : '#e0f2fe', icon: <BarChartOutlined /> },
  ];

  return (
    <Spin spinning={loading}>
    <div style={{ padding: '20px 24px' }}>

      {/* Filter bar */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '14px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#64748b' }}>Fiscal Year:</span>
        <Select value={fiscalYear} onChange={setFiscalYear} style={{ width: 140 }}>
          {getFiscalYearOptions()}
        </Select>
        <Button icon={<ReloadOutlined />} onClick={fetchBudgetReport}
          style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff', borderRadius: 8, fontWeight: 600 }}>
          Generate
        </Button>
      </div>

      {/* Summary stat cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        {statCards.map((sc, i) => (
          <Col key={i} xs={12} sm={6}>
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', borderTop: `3px solid ${sc.accent}`, padding: '14px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{sc.label}</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: sc.accent }}>{sc.value}</div>
                </div>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: sc.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: sc.accent }}>
                  {sc.icon}
                </div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* Budget table */}
      <Card title={<span style={{ color: '#7a0000', fontWeight: 700, fontSize: 14 }}>Budget Utilization — FY {fiscalYear}</span>}
        headStyle={{ borderBottom: '1.5px solid #e2e8f0' }} size="small" style={{ marginBottom: 12 }}>
        <Table columns={columns} size="small" scroll={{ x: 'max-content' }} pagination={{ pageSize: 10 }}
          dataSource={budgetData.map((item, i) => ({ key: i, ...item }))}
          summary={() => (
            <Table.Summary.Row style={{ background: '#fafafa' }}>
              <Table.Summary.Cell><strong>Total</strong></Table.Summary.Cell>
              <Table.Summary.Cell><strong style={{ color: '#7a0000' }}>{fmt(summaryStats.totalAllocated)}</strong></Table.Summary.Cell>
              <Table.Summary.Cell><strong style={{ color: '#52c41a' }}>{fmt(summaryStats.totalSpent)}</strong></Table.Summary.Cell>
              <Table.Summary.Cell><strong style={{ color: '#fa8c16' }}>{fmt(summaryStats.totalRemaining)}</strong></Table.Summary.Cell>
              <Table.Summary.Cell><strong>{summaryStats.avgUtilization.toFixed(1)}%</strong></Table.Summary.Cell>
              <Table.Summary.Cell>
                {summaryStats.avgUtilization > 80 ? <Tag color="warning">Monitor</Tag> : <Tag color="success">Healthy</Tag>}
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      </Card>

      {/* Health summary */}
      <Card title={<span style={{ color: '#7a0000', fontWeight: 700, fontSize: 14 }}>Budget Health Summary</span>}
        headStyle={{ borderBottom: '1.5px solid #e2e8f0' }} size="small">
        <Row gutter={[16, 0]}>
          <Col xs={8} style={{ textAlign: 'center', padding: '14px 0' }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px', fontSize: 20, color: '#15803d' }}><CheckCircleOutlined /></div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#15803d' }}>{stats.onTrack}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>On Track</div>
          </Col>
          <Col xs={8} style={{ textAlign: 'center', padding: '14px 0' }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px', fontSize: 20, color: '#d97706' }}><WarningOutlined /></div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#d97706' }}>{stats.highUsage}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>High Usage</div>
          </Col>
          <Col xs={8} style={{ textAlign: 'center', padding: '14px 0' }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: '#eef2f8', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px', fontSize: 20, color: '#7a0000' }}>—</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#7a0000' }}>{stats.notStarted}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Not Started</div>
          </Col>
        </Row>
      </Card>
    </div>
    </Spin>
  );
}

export default BudgetReports;
